import { lazy, Suspense } from "react";
import {
  Navigate,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { App } from "@/App";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { PrivateLayout } from "@/components/layout/PrivateLayout";
import { LoadingState } from "@/components/LoadingState";
import { analyticsPages } from "@/lib/analytics-pages";
import { getMe, type MeResponse } from "@/lib/api";
import { queryClient } from "@/lib/query";
import { LoginPage } from "@/routes/login";

function requireRoutePermission(permission: string) {
  return () => {
    const me = queryClient.getQueryData<MeResponse>(["me"]);
    if (!me?.permissions.includes(permission)) {
      throw redirect({ to: "/" });
    }
  };
}

const LazyAnalyticsDashboardPage = lazy(() =>
  import("@/routes/analytics-dashboard").then((m) => ({ default: m.AnalyticsDashboardPage })),
);
const LazyPlaceholderPage = lazy(() =>
  import("@/routes/placeholder").then((m) => ({ default: m.PlaceholderPage })),
);

const rootRoute = createRootRoute({
  component: App,
});

// -- Public branch (login, future legal pages) --

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: PublicLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/login",
  component: LoginPage,
});

// -- Private branch (auth-guarded) --

const privateLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "private",
  component: PrivateLayout,
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({ queryKey: ["me"], queryFn: getMe });
    } catch {
      throw redirect({ to: "/login", search: { return_to: window.location.pathname } });
    }
  },
});

const INDEX_FALLBACKS: { permission: string; path: string }[] = [
  { permission: "analytics:view", path: analyticsPages.onShelfAvailability.path },
  { permission: "incidents:view", path: "/incidents" },
  { permission: "opportunities:view", path: "/opportunities" },
];

const indexRoute = createRoute({
  getParentRoute: () => privateLayoutRoute,
  path: "/",
  component: () => {
    const me = queryClient.getQueryData<MeResponse>(["me"]);
    const permissions = me?.permissions ?? [];
    const target = INDEX_FALLBACKS.find((f) => permissions.includes(f.permission));
    return <Navigate to={target?.path ?? "/login"} replace />;
  },
});

const analyticsRoutes = Object.values(analyticsPages).map((page) =>
  createRoute({
    getParentRoute: () => privateLayoutRoute,
    path: page.path,
    beforeLoad: requireRoutePermission("analytics:view"),
    component: () => (
      <Suspense fallback={<LoadingState />}>
        <LazyAnalyticsDashboardPage page={page} />
      </Suspense>
    ),
  }),
);

const incidentsRoute = createRoute({
  getParentRoute: () => privateLayoutRoute,
  path: "/incidents",
  beforeLoad: requireRoutePermission("incidents:view"),
  component: () => (
    <Suspense fallback={<LoadingState />}>
      <LazyPlaceholderPage title="Incidents" />
    </Suspense>
  ),
});

const opportunitiesRoute = createRoute({
  getParentRoute: () => privateLayoutRoute,
  path: "/opportunities",
  beforeLoad: requireRoutePermission("opportunities:view"),
  component: () => (
    <Suspense fallback={<LoadingState />}>
      <LazyPlaceholderPage title="Opportunities" />
    </Suspense>
  ),
});

const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([loginRoute]),
  privateLayoutRoute.addChildren([
    indexRoute,
    ...analyticsRoutes,
    incidentsRoute,
    opportunitiesRoute,
  ]),
]);

export const router = createRouter({ routeTree });

export function AppRouterProvider() {
  return <RouterProvider router={router} />;
}
